import {inject, Injectable} from '@angular/core';
import {catchError, map, Observable, of} from 'rxjs';
import {Router} from '@angular/router';
import {ShineAuthClient} from './shine-auth-client';

/**
 * Service for handling authentication with the Shine Auth service.
 */
@Injectable({
    providedIn: 'root'
})
export class ShineAuthService {
    private shineAuthClient = inject(ShineAuthClient);
    private router = inject(Router);

    private readonly tokenKey = 'shine_auth_token';
    private readonly refreshTokenKey = 'shine_auth_refresh_token';

    /**
     * Authenticates a user with username and password.
     * @param username The username.
     * @param password The password.
     * @returns An observable that resolves to a boolean indicating success.
     */
    login(username: string, password: string): Observable<boolean> {
        return this.shineAuthClient.login(username, password).pipe(
            map(result => {
                if (result.accessToken) {
                    localStorage.setItem(this.tokenKey, result.accessToken);
                    localStorage.setItem(this.refreshTokenKey, result.refreshToken);
                    return true;
                }
                return false;
            }),
            catchError(error => {
                console.error('Error authenticating user:', error);
                return of(false);
            })
        );
    }

    /**
     * Logs out the current user.
     * @returns An observable that resolves to a boolean indicating success.
     */
    logout(): Observable<boolean> {
        console.debug('Logging out user');

        const refreshToken = localStorage.getItem(this.refreshTokenKey);
        if (!refreshToken) {
            this.clearTokens();
            return of(true);
        }

        return this.shineAuthClient.logout(refreshToken).pipe(
            map(result => {
                this.clearTokens();
                return result.success;
            }),
            catchError(error => {
                console.error('Error logging out user:', error);
                this.clearTokens();
                return of(false);
            })
        );
    }

    /**
     * Refreshes the authentication token.
     * @returns An observable that resolves to a boolean indicating success.
     */
    refreshToken(): Observable<boolean> {
        console.debug('Refreshing token');

        const refreshToken = localStorage.getItem(this.refreshTokenKey);
        if (!refreshToken) {
            console.warn('No refresh token available');
            return of(false);
        }

        return this.shineAuthClient.refreshToken(refreshToken).pipe(
            map(result => {
                if (result.accessToken) {
                    localStorage.setItem(this.tokenKey, result.accessToken);
                    localStorage.setItem(this.refreshTokenKey, result.refreshToken);
                    return true;
                }
                return false;
            }),
            catchError(error => {
                console.error('Error refreshing token:', error);
                return of(false);
            })
        );
    }

    /**
     * Checks if the user is authenticated.
     * @returns True if the user is authenticated, false otherwise.
     */
    isAuthenticated(): boolean {
        return !!localStorage.getItem(this.tokenKey);
    }

    /**
     * Gets the authentication token.
     * @returns The authentication token, or null if not available.
     */
    getToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    /**
     * Clears all authentication tokens.
     */
    private clearTokens(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.refreshTokenKey);
    }
}